/**
 * Marketplace Analytics Dashboard
 * Shows marketplace performance metrics and charts
 * Uses real API data from /leadflow/marketplace/analytics
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { SelectButton } from 'primereact/selectbutton';
import { LeadGradeBadge } from './components';
import {
    marketplaceService,
    formatLoanAmount,
    getLoanProductConfig
} from '../../services/marketplaceService';

const Analytics = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('30d');
    const [myListingsOnly, setMyListingsOnly] = useState(true);
    const [analytics, setAnalytics] = useState(null);

    const viewOptions = [
        { label: 'My Listings', value: true },
        { label: 'All Marketplace', value: false }
    ];

    const periodOptions = [
        { label: 'Last 7 Days', value: '7d' },
        { label: 'Last 30 Days', value: '30d' },
        { label: 'Last 90 Days', value: '90d' }
    ];

    // Fetch analytics
    const fetchAnalytics = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await marketplaceService.getAnalytics({
                period,
                agent_only: myListingsOnly
            });
            if (response.success) {
                setAnalytics(response.data);
            } else {
                setError(response.message || 'Failed to load analytics');
            }
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            setError('Failed to load analytics. Please try again.');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load analytics data'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [period, myListingsOnly]);

    // Chart configurations - using API response structure
    const getViewsTrendData = () => {
        const trends = analytics?.views?.daily_trend || [];
        return {
            labels: trends.map(t => t.date || t.label),
            datasets: [
                {
                    label: 'Views',
                    data: trends.map(t => t.views || t.count || 0),
                    fill: true,
                    borderColor: '#3B82F6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4
                }
            ]
        };
    };

    const getInterestTrendData = () => {
        const trends = analytics?.interest?.daily_trend || [];
        return {
            labels: trends.map(t => t.date || t.label),
            datasets: [
                {
                    label: 'Enquiries',
                    data: trends.map(t => t.interest || t.count || 0),
                    fill: true,
                    borderColor: '#EC4899',
                    backgroundColor: 'rgba(236, 72, 153, 0.1)',
                    tension: 0.4
                }
            ]
        };
    };

    const getLeadTypesData = () => {
        const types = analytics?.popular_lead_types || [];
        return {
            labels: types.map(t => getLoanProductConfig(t.product_type).label),
            datasets: [{
                data: types.map(t => t.count || 0),
                backgroundColor: ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4']
            }]
        };
    };

    const getTimeToPurchaseData = () => {
        const data = analytics?.time_to_purchase?.distribution || [];
        return {
            labels: data.map(d => d.range || d.label),
            datasets: [{
                label: 'Purchases',
                data: data.map(d => d.count || 0),
                backgroundColor: '#22C55E'
            }]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' }
        }
    };

    // Loading skeleton
    if (loading) {
        return (
            <div className="marketplace-analytics">
                <div className="flex justify-content-between mb-4">
                    <Skeleton width="200px" height="2rem" />
                    <Skeleton width="300px" height="2.5rem" />
                </div>
                <div className="grid">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="col-12 md:col-6 xl:col-3">
                            <Card><Skeleton height="100px" /></Card>
                        </div>
                    ))}
                </div>
                <div className="grid mt-3">
                    <div className="col-12 lg:col-8"><Card><Skeleton height="300px" /></Card></div>
                    <div className="col-12 lg:col-4"><Card><Skeleton height="300px" /></Card></div>
                </div>
            </div>
        );
    }

    // Error state
    if (error && !analytics) {
        return (
            <div className="marketplace-analytics">
                <div className="flex flex-column align-items-center justify-content-center py-8">
                    <div className="bg-red-50 border-circle p-5 mb-4">
                        <i className="pi pi-exclamation-triangle text-red-500 text-5xl" />
                    </div>
                    <h3 className="text-900 font-semibold mb-2">Failed to Load Analytics</h3>
                    <p className="text-600 mb-4 text-center" style={{ maxWidth: '400px' }}>
                        {error}
                    </p>
                    <div className="flex gap-2">
                        <Button
                            label="Try Again"
                            icon="pi pi-refresh"
                            onClick={fetchAnalytics}
                        />
                        <Button
                            label="Back to Marketplace"
                            icon="pi pi-arrow-left"
                            className="p-button-outlined"
                            onClick={() => navigate('/app/marketplace')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Empty state - no data but no error
    if (!analytics) {
        return (
            <div className="marketplace-analytics">
                <div className="flex flex-column align-items-center justify-content-center py-8">
                    <div className="bg-blue-50 border-circle p-5 mb-4">
                        <i className="pi pi-chart-bar text-blue-500 text-5xl" />
                    </div>
                    <h3 className="text-900 font-semibold mb-2">No Analytics Data</h3>
                    <p className="text-600 mb-4 text-center" style={{ maxWidth: '400px' }}>
                        Start posting leads to the marketplace to see your analytics here.
                    </p>
                    <Button
                        label="Post Your First Lead"
                        icon="pi pi-plus"
                        onClick={() => navigate('/app/marketplace/post')}
                    />
                </div>
            </div>
        );
    }

    const summary = analytics?.summary || {};
    const views = analytics?.views || {};
    const interest = analytics?.interest || {};
    const timeToPurchase = analytics?.time_to_purchase || {};

    return (
        <div className="marketplace-analytics">
            <Toast ref={toast} />

            {/* Header */}
            <div className="surface-card border-round shadow-1 p-4 mb-4">
                <div className="flex flex-column lg:flex-row lg:align-items-center lg:justify-content-between gap-3">
                    <div className="flex align-items-center gap-2">
                        <Button
                            icon="pi pi-arrow-left"
                            className="p-button-text p-button-rounded"
                            onClick={() => navigate('/app/marketplace')}
                            tooltip="Back to Marketplace"
                        />
                        <div>
                            <h2 className="text-900 font-bold m-0 mb-1">Marketplace Analytics</h2>
                            <p className="text-600 m-0">Track your marketplace performance</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <SelectButton
                            value={myListingsOnly}
                            options={viewOptions}
                            onChange={(e) => e.value !== null && setMyListingsOnly(e.value)}
                            optionLabel="label"
                            optionValue="value"
                        />
                        <Dropdown
                            value={period}
                            options={periodOptions}
                            onChange={(e) => setPeriod(e.value)}
                            className="w-10rem"
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-outlined"
                            onClick={fetchAnalytics}
                            loading={loading}
                            tooltip="Refresh"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid mb-4">
                {[
                    {
                        label: 'Total Listings',
                        value: summary.total_listings || views.total_listings || 0,
                        icon: 'pi-list',
                        color: 'blue',
                        bgColor: '#eff6ff'
                    },
                    {
                        label: 'Total Views',
                        value: (summary.total_views || views.total_views || 0).toLocaleString(),
                        icon: 'pi-eye',
                        color: 'purple',
                        bgColor: '#faf5ff'
                    },
                    {
                        label: 'Total Enquiries',
                        value: summary.total_interest || interest.total_interest || 0,
                        icon: 'pi-heart',
                        color: 'pink',
                        bgColor: '#fdf2f8'
                    },
                    {
                        label: 'Conversion Rate',
                        value: `${(summary.conversion_rate || 0).toFixed(1)}%`,
                        icon: 'pi-percentage',
                        color: 'green',
                        bgColor: '#f0fdf4'
                    }
                ].map((stat, idx) => (
                    <div key={idx} className="col-12 md:col-6 xl:col-3">
                        <Card className="shadow-1 h-full">
                            <div className="flex align-items-center gap-3">
                                <div
                                    className="flex align-items-center justify-content-center border-round-lg"
                                    style={{
                                        width: '56px',
                                        height: '56px',
                                        backgroundColor: stat.bgColor
                                    }}
                                >
                                    <i className={`pi ${stat.icon} text-${stat.color}-500 text-2xl`} />
                                </div>
                                <div>
                                    <div className="text-500 text-sm mb-1">{stat.label}</div>
                                    <div className="text-900 font-bold text-2xl">{stat.value}</div>
                                </div>
                            </div>
                        </Card>
                    </div>
                ))}
            </div>

            {/* Charts Row */}
            <div className="grid mb-4">
                {/* Views Trend */}
                <div className="col-12 lg:col-8">
                    <Card className="shadow-1 h-full">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <h4 className="text-900 font-semibold m-0">Views Trend</h4>
                            <Tag value={`${views.total_views || 0} total`} severity="info" />
                        </div>
                        <div style={{ height: '300px' }}>
                            {analytics?.views?.daily_trend?.length > 0 ? (
                                <Chart type="line" data={getViewsTrendData()} options={chartOptions} />
                            ) : (
                                <div className="flex align-items-center justify-content-center h-full text-500">
                                    <div className="text-center">
                                        <i className="pi pi-chart-line text-4xl mb-2" />
                                        <p>No view data available for this period</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Lead Types Distribution */}
                <div className="col-12 lg:col-4">
                    <Card className="shadow-1 h-full">
                        <h4 className="text-900 font-semibold m-0 mb-3">Popular Lead Types</h4>
                        <div style={{ height: '300px' }}>
                            {analytics?.popular_lead_types?.length > 0 ? (
                                <Chart type="doughnut" data={getLeadTypesData()} options={chartOptions} />
                            ) : (
                                <div className="flex align-items-center justify-content-center h-full text-500">
                                    <div className="text-center">
                                        <i className="pi pi-chart-pie text-4xl mb-2" />
                                        <p>No data available</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>

            {/* Second Row */}
            <div className="grid mb-4">
                {/* Time to Purchase */}
                <div className="col-12 lg:col-6">
                    <Card className="shadow-1">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <h4 className="text-900 font-semibold m-0">Time to Purchase</h4>
                            {timeToPurchase.average_hours && (
                                <Tag
                                    value={`Avg: ${Math.round(timeToPurchase.average_hours)}h`}
                                    severity="success"
                                />
                            )}
                        </div>
                        <div style={{ height: '250px' }}>
                            {analytics?.time_to_purchase?.distribution?.length > 0 ? (
                                <Chart type="bar" data={getTimeToPurchaseData()} options={chartOptions} />
                            ) : (
                                <div className="flex align-items-center justify-content-center h-full text-500">
                                    <div className="text-center">
                                        <i className="pi pi-clock text-4xl mb-2" />
                                        <p>No purchase data available</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Top Listings */}
                <div className="col-12 lg:col-6">
                    <Card className="shadow-1">
                        <h4 className="text-900 font-semibold m-0 mb-3">Top Performing Listings</h4>
                        {views.top_viewed?.length > 0 ? (
                            <DataTable
                                value={views.top_viewed.slice(0, 5)}
                                responsiveLayout="scroll"
                                className="p-datatable-sm"
                            >
                                <Column
                                    header="Lead"
                                    body={(row) => (
                                        <div>
                                            <div className="font-semibold text-900 text-sm">
                                                {row.borrower_name || 'Lead'}
                                            </div>
                                            <div className="text-500 text-xs">
                                                {getLoanProductConfig(row.product_type).label}
                                            </div>
                                        </div>
                                    )}
                                />
                                <Column
                                    header="Grade"
                                    body={(row) => <LeadGradeBadge grade={row.quality_grade} size="small" />}
                                    style={{ width: '80px' }}
                                />
                                <Column
                                    header="Views"
                                    body={(row) => (
                                        <span className="flex align-items-center gap-1">
                                            <i className="pi pi-eye text-500 text-xs" />
                                            {row.view_count || 0}
                                        </span>
                                    )}
                                    style={{ width: '80px' }}
                                />
                                <Column
                                    header="Enquiries"
                                    body={(row) => (
                                        <span className="flex align-items-center gap-1 text-pink-500">
                                            <i className="pi pi-heart text-xs" />
                                            {row.interest_count || 0}
                                        </span>
                                    )}
                                    style={{ width: '80px' }}
                                />
                            </DataTable>
                        ) : (
                            <div className="flex align-items-center justify-content-center py-6 text-500">
                                <div className="text-center">
                                    <i className="pi pi-list text-4xl mb-2" />
                                    <p>No listings data available</p>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* Enquiry Trend */}
            <div className="grid mb-4">
                <div className="col-12">
                    <Card className="shadow-1">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <h4 className="text-900 font-semibold m-0">Enquiry Trend</h4>
                            <Tag value={`${interest.total_interest || 0} total`} severity="help" />
                        </div>
                        <div style={{ height: '250px' }}>
                            {analytics?.interest?.daily_trend?.length > 0 ? (
                                <Chart type="line" data={getInterestTrendData()} options={chartOptions} />
                            ) : (
                                <div className="flex align-items-center justify-content-center h-full text-500">
                                    <div className="text-center">
                                        <i className="pi pi-heart text-4xl mb-2" />
                                        <p>No enquiry data available for this period</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>

            {/* Performance by Product Type */}
            {analytics?.popular_lead_types?.length > 0 && (
                <Card className="shadow-1">
                    <h4 className="text-900 font-semibold m-0 mb-3">Performance by Product Type</h4>
                    <DataTable
                        value={analytics.popular_lead_types}
                        responsiveLayout="scroll"
                        className="p-datatable-sm"
                    >
                        <Column
                            header="Product Type"
                            body={(row) => {
                                const config = getLoanProductConfig(row.product_type);
                                return (
                                    <Tag
                                        value={config.label}
                                        icon={`pi ${config.icon}`}
                                        severity="info"
                                        className="text-xs"
                                    />
                                );
                            }}
                        />
                        <Column field="count" header="Listings" sortable />
                        <Column field="total_views" header="Views" sortable />
                        <Column field="total_interest" header="Enquiries" sortable />
                        <Column field="purchases" header="Purchases" sortable />
                        <Column
                            header="Conversion"
                            body={(row) => {
                                const viewCount = row.total_views || 1;
                                const purchases = row.purchases || 0;
                                const rate = ((purchases / viewCount) * 100).toFixed(1);
                                return (
                                    <Tag
                                        value={`${rate}%`}
                                        severity={rate > 5 ? 'success' : rate > 2 ? 'warning' : 'danger'}
                                    />
                                );
                            }}
                        />
                    </DataTable>
                </Card>
            )}
        </div>
    );
};

export default Analytics;
